import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { CachingService } from '@nk/caching-lib';
import {
  AppFeatureKey,
  CompanyCommonAttributeEntity,
  CompanyCommonMeasureEntity,
  CompanyCustomerAttributeEntity,
  CompanyCustomerData,
  CompanyCustomerMeasureEntity,
  COUNTRY_FEATURES,
} from '@nk/data-entities-lib';
import { arrayJoin } from '@nk/utils-lib';
import { forkJoin, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Injectable()
export class CompanyAttributeService {
  constructor(
    private _api: ApplicationApiClient,
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
  ) {}

  public companyCustomerMeasureListByAppCustomerAndCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyCustomerMeasureListByAppCustomerAndCompany,
  );
  public fetchCompanyCustomerMeasureListByAppCustomerAndCompany(
    customerUuid: string,
    companyIdentifier: string,
  ): Observable<CompanyCustomerMeasureEntity[]> {
    return this._api.get<CompanyCustomerMeasureEntity[]>(
      `app-customer/${customerUuid}/company/${companyIdentifier}/company-customer-measure?latest_measure_flag=1`,
    ); // Feature access not required
  }

  public companyCustomerAttributeListByAppCustomerCache = this._cachingService.create(
    this,
    this.fetchCompanyCustomerAttributeListByAppCustomer,
  );
  public fetchCompanyCustomerAttributeListByAppCustomer(
    customerUuid: string,
  ): Observable<CompanyCustomerAttributeEntity[]> {
    return this._api.get<CompanyCustomerAttributeEntity[]>(`app-customer/${customerUuid}/company-customer-attribute`);
  }

  public companyCustomerDataListByAppCustomerAndCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyCustomerDataListByAppCustomerAndCompany,
  );
  public fetchCompanyCustomerDataListByAppCustomerAndCompany(
    customerUuid: string,
    companyIdentifier: string,
  ): Observable<CompanyCustomerData[]> {
    return forkJoin([
      this.companyCustomerMeasureListByAppCustomerAndCompanyCache.get(customerUuid, companyIdentifier).pipe(first()),
      this.companyCustomerAttributeListByAppCustomerCache.get(customerUuid).pipe(first()),
    ]).pipe(
      map(([companyCustomerMeasureList, companyCustomerAttributeList]) => {
        return arrayJoin(
          companyCustomerMeasureList,
          companyCustomerAttributeList,
          (m) => m.company_customer_attribute_uuid,
          (a) => a.uuid,
        ).map((p) => new CompanyCustomerData(p.left, p.right));
      }),
    );
  }

  public companyCommonMeasureListByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyCommonMeasureListByCompany,
  );
  public fetchCompanyCommonMeasureListByCompany(companyUuid: string): Observable<CompanyCommonMeasureEntity[]> {
    return this._analysisApiAccess.get<CompanyCommonMeasureEntity[]>(
      `company/${companyUuid}/company-common-measure?latest_measure_flag=1`,
      {},
      [AppFeatureKey.base, ...COUNTRY_FEATURES],
    );
  }

  public companyCommonAttributeListCache = this._cachingService.create(this, this.fetchCompanyCommonAttributeList);
  public fetchCompanyCommonAttributeList(): Observable<CompanyCommonAttributeEntity[]> {
    return this._analysisApiAccess.get<CompanyCommonAttributeEntity[]>('company-common-attribute');
  }
}
